import {
  createContext,
  type PropsWithChildren,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  getCsrfToken,
  getCurrentUser,
  heartbeat,
  login as loginRequest,
  logout as logoutRequest,
  setActiveCompany as setActiveCompanyRequest,
  setCulture as setCultureRequest,
  setTheme as setThemeRequest,
} from './authApi'
import type { AuthenticatedUser, LoginRequest } from './types'
import { sessionInvalidatedEvent } from '../../shared/http/apiFetch'

type AuthContextValue = {
  user: AuthenticatedUser | null
  isLoading: boolean
  sessionEndReason: string | null
  acknowledgeSessionEnd: () => void
  login: (request: LoginRequest) => Promise<void>
  logout: () => Promise<void>
  selectCompany: (companyId: string) => Promise<void>
  selectCulture: (culture: string) => Promise<void>
  selectTheme: (themeMode: 'System' | 'Light' | 'Dark') => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)
const sessionIdleTimeoutMs = 30 * 60 * 1000
const heartbeatIntervalMs = 30 * 1000
const activityWriteThrottleMs = 1000

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null)
  const [csrfToken, setCsrfToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [sessionEndReason, setSessionEndReason] = useState<string | null>(null)
  const sessionInvalidatedRef = useRef(false)

  const invalidateSession = useCallback((reason = 'auth.sessionEndedByAnotherLogin') => {
    if (sessionInvalidatedRef.current) return
    sessionInvalidatedRef.current = true
    setSessionEndReason(reason)
    setUser(null)
    setCsrfToken(null)
    getCsrfToken().then(setCsrfToken).catch(() => setCsrfToken(null))
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    Promise.all([getCsrfToken(), getCurrentUser(controller.signal)])
      .then(([token, currentUser]) => {
        setCsrfToken(token)
        setUser(currentUser)
      })
      .catch(() => {
        setUser(null)
      })
      .finally(() => setIsLoading(false))

    return () => controller.abort()
  }, [])

  useEffect(() => {
    const handleInvalidation = () => invalidateSession()
    window.addEventListener(sessionInvalidatedEvent, handleInvalidation)
    return () => window.removeEventListener(sessionInvalidatedEvent, handleInvalidation)
  }, [invalidateSession])

  useEffect(() => {
    if (!user || !csrfToken) {
      return
    }

    const activityStorageKey = `apl-enterprise:last-activity:${user.configurationId}:${user.id}`
    let lastActivityAt = Date.now()
    let lastStoredAt = 0

    const readSharedActivity = () => {
      try {
        const stored = Number(window.localStorage.getItem(activityStorageKey))
        return Number.isFinite(stored) ? stored : 0
      } catch {
        return 0
      }
    }

    const recordActivity = () => {
      const now = Date.now()
      lastActivityAt = now
      if (now - lastStoredAt >= activityWriteThrottleMs) {
        lastStoredAt = now
        try {
          window.localStorage.setItem(activityStorageKey, String(now))
        } catch {
          // Storage may be unavailable in hardened/private browser contexts.
        }
      }
    }

    recordActivity()
    const activityEvents: Array<keyof WindowEventMap> = [
      'pointerdown',
      'keydown',
      'touchstart',
      'wheel',
    ]
    activityEvents.forEach((eventName) =>
      window.addEventListener(eventName, recordActivity, { passive: true }))

    const timer = window.setInterval(() => {
      const mostRecentActivityAt = Math.max(lastActivityAt, readSharedActivity())
      if (Date.now() - mostRecentActivityAt >= sessionIdleTimeoutMs) {
        logoutRequest(csrfToken).catch(() => undefined)
        invalidateSession('auth.sessionEndedByInactivity')
        return
      }

      heartbeat(csrfToken)
        .then((status) => {
          if (status === 'expired') {
            invalidateSession()
          }
        })
        .catch(() => {
          // A transient network failure must not terminate a valid session.
        })
    }, heartbeatIntervalMs)

    return () => {
      window.clearInterval(timer)
      activityEvents.forEach((eventName) =>
        window.removeEventListener(eventName, recordActivity))
    }
  }, [csrfToken, invalidateSession, user])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      sessionEndReason,
      acknowledgeSessionEnd: () => setSessionEndReason(null),
      login: async (request) => {
        // Antiforgery tokens are identity-bound. Request a fresh token before
        // every login so an expired session cannot poison reauthentication.
        let token = await getCsrfToken()
        let authenticatedUser: AuthenticatedUser
        try {
          authenticatedUser = await loginRequest(request, token)
        } catch (reason) {
          if (!(reason instanceof Error) || reason.message !== 'auth.csrfUnavailable') {
            throw reason
          }
          // The server may have cleared an expired authentication cookie while
          // validating the request. Refresh the antiforgery pair once and
          // retry without making the user reload the page.
          token = await getCsrfToken()
          authenticatedUser = await loginRequest(request, token)
        }
        setUser(authenticatedUser)
        sessionInvalidatedRef.current = false
        setSessionEndReason(null)
        try {
          setCsrfToken(await getCsrfToken())
        } catch {
          setCsrfToken(null)
        }
      },
      logout: async () => {
        if (csrfToken) {
          await logoutRequest(csrfToken)
        }
        setUser(null)
        setCsrfToken(await getCsrfToken())
      },
      selectCompany: async (companyId) => {
        const token = csrfToken ?? (await getCsrfToken())
        await setActiveCompanyRequest(companyId, token)
        setUser((current) => current
          ? { ...current, activeCompanyId: companyId }
          : current)
      },
      selectCulture: async (culture) => {
        const token = csrfToken ?? (await getCsrfToken())
        await setCultureRequest(culture, token)
        setUser((current) => current ? { ...current, culture } : current)
      },
      selectTheme: async (themeMode) => {
        const token = csrfToken ?? (await getCsrfToken())
        await setThemeRequest(themeMode, token)
        setUser((current) => current ? { ...current, themeMode } : current)
      },
    }),
    [csrfToken, isLoading, sessionEndReason, user],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider')
  }
  return context
}
